'use client';

import { useEffect, useId, useRef, useState } from 'react';
import Script from 'next/script';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { loginWithYandexAccessToken, type OAuthProvider } from '@/lib/oauth';
import { getSafeRedirectPath } from '@/lib/auth-redirect';

const YANDEX_SUGGEST_SDK =
  'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js';

declare global {
  interface Window {
    YaAuthSuggest?: {
      init: (
        oauthQueryParams: Record<string, string>,
        tokenPageOrigin: string,
        suggestParams?: Record<string, string | number>,
      ) => Promise<{ handler: () => Promise<YandexSuggestTokenPayload> }>;
    };
  }
}

interface YandexSuggestTokenPayload {
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  error?: string;
  [key: string]: unknown;
}

interface YandexSuggestButtonProps {
  provider: OAuthProvider;
  redirectPath?: string;
}

export function YandexSuggestButton({
  provider,
  redirectPath = '/',
}: YandexSuggestButtonProps) {
  const router = useRouter();
  const reactId = useId().replace(/:/g, '');
  const containerId = `yandex-oauth-btn-${reactId}`;
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const initStarted = useRef(false);

  const clientId = provider.client_id;
  const suggestRedirectUri = provider.suggest_redirect_uri;

  useEffect(() => {
    if (typeof window !== 'undefined' && window.YaAuthSuggest?.init) {
      setSdkReady(true);
    }
  }, []);

  useEffect(() => {
    if (!sdkReady || !clientId || !suggestRedirectUri || initStarted.current) {
      return;
    }

    if (!window.YaAuthSuggest?.init) {
      setError('Не удалось загрузить SDK Яндекс ID');
      return;
    }

    initStarted.current = true;
    // origin страницы с кнопкой (login) — для postMessage из suggest-token
    const tokenPageOrigin = window.location.origin;

    void window.YaAuthSuggest.init(
      {
        client_id: clientId,
        response_type: 'token',
        redirect_uri: suggestRedirectUri,
      },
      tokenPageOrigin,
      {
        view: 'button',
        parentId: containerId,
        buttonSize: 'm',
        buttonView: 'main',
        buttonTheme: 'light',
        buttonBorderRadius: 12,
        buttonIcon: 'ya',
      },
    )
      .then(({ handler }) => handler())
      .then(async (data: YandexSuggestTokenPayload) => {
        const accessToken = typeof data.access_token === 'string' ? data.access_token : '';
        if (!accessToken) {
          setError('Яндекс ID не вернул токен');
          return;
        }

        setBusy(true);
        setError(null);
        try {
          const result = await loginWithYandexAccessToken(
            accessToken,
            getSafeRedirectPath(redirectPath),
          );
          router.replace(getSafeRedirectPath(result.redirect ?? redirectPath));
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Ошибка входа через Яндекс ID');
          initStarted.current = false;
        } finally {
          setBusy(false);
        }
      })
      .catch((err: unknown) => {
        const message =
          err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
            ? err.message
            : 'Авторизация через Яндекс ID отменена или недоступна';
        setError(message);
        initStarted.current = false;
      });
  }, [sdkReady, clientId, suggestRedirectUri, containerId, redirectPath, router]);

  if (!clientId || !suggestRedirectUri) {
    return null;
  }

  return (
    <div className="space-y-2">
      <Script
        src={YANDEX_SUGGEST_SDK}
        strategy="afterInteractive"
        onLoad={() => setSdkReady(true)}
        onError={() => setError('Не удалось загрузить SDK Яндекс ID')}
      />
      <div
        id={containerId}
        className="flex min-h-10 w-full items-center justify-center [&_iframe]:!w-full"
      />
      {busy && (
        <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          <span>Входим через Яндекс ID…</span>
        </div>
      )}
      {error && (
        <p className="text-center text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
